import { useHistory } from 'react-router-dom';
import { createConversation } from '../../../../api/conversations';
import { COMMERCIAL_NO_MENTOR_BODY, COMMERCIAL_NO_MENTOR_HEADING } from '../../../../helpers/no_mentor_copy';
import { updateMatchAndSet } from '../../../../helpers/mentor_match_helper';
import { buildMentorFirstMessage } from '../../../../helpers/mentor_first_message';

import React, { useEffect, useRef, useState } from 'react';
import SignupMentorMatch from '../../../core/compositions/signup_mentor_match';
import NoMentorFoundMessage from './no_mentor_found_message';

const PROGRESS_TICK_MS = 60;
const PROGRESS_CEILING_BEFORE_MATCH = 95;
const STEP_COUNT = 3;
const STEP_DURATION_MS = 1500;
const STEPS_TOTAL_MS = STEP_DURATION_MS * STEP_COUNT;
const REVEAL_PAUSE_MS = 600;
const SEARCH_MIN_MS = STEPS_TOTAL_MS + REVEAL_PAUSE_MS;

const metaParts = (values) =>
  (Array.isArray(values) ? values : [values])
    .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
    .filter(Boolean);

const buildMentorMeta = (patientPartner) => {
  const conditions = metaParts(patientPartner.conditions);
  const focusAreas = metaParts(patientPartner.focus_areas);
  const treatments = focusAreas.length > 0 ? focusAreas : metaParts(patientPartner.surgery);

  return [patientPartner.display_age, ...conditions, ...treatments]
    .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' · ');
};

export default function MentorMatchV2({
  patientPartner,
  mentorFound,
  mentorMatch,
  setMentorMatch,
  sessionStoragePrefix,
  onNoMentorFound,
  noMentorHeading,
  noMentorBody,
  firstMessageAnswer,
}) {
  const history = useHistory();

  const [progress, setProgress] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [searchAnimationComplete, setSearchAnimationComplete] = useState(false);
  const [noMentorFound, setNoMentorFound] = useState(false);
  const handoffStartedRef = useRef(false);

  const view = patientPartner && searchAnimationComplete ? 'matched' : 'searching';

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      if (elapsed < STEPS_TOTAL_MS) {
        const stepIndex = Math.min(STEP_COUNT - 1, Math.floor(elapsed / STEP_DURATION_MS));
        const pct = Math.min(
          PROGRESS_CEILING_BEFORE_MATCH,
          Math.round((elapsed / STEPS_TOTAL_MS) * PROGRESS_CEILING_BEFORE_MATCH)
        );
        setActiveStepIndex(stepIndex);
        setProgress(pct);
      } else {
        setActiveStepIndex(STEP_COUNT);
        setProgress(PROGRESS_CEILING_BEFORE_MATCH);
        if (elapsed >= SEARCH_MIN_MS) {
          setSearchAnimationComplete(true);
          clearInterval(id);
        }
      }
    }, PROGRESS_TICK_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (mentorFound === false && !patientPartner) {
      if (onNoMentorFound) {
        onNoMentorFound();
      } else {
        setNoMentorFound(true);
      }
    }
  }, [mentorFound, patientPartner, onNoMentorFound]);

  const cleanupSession = () => {
    if (sessionStoragePrefix) {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith(sessionStoragePrefix))
        .forEach((k) => sessionStorage.removeItem(k));
    }
    sessionStorage.removeItem('mentorMatch');
    sessionStorage.removeItem('patientPartner');
  };

  const handoff = () => {
    if (handoffStartedRef.current || !patientPartner) return;
    handoffStartedRef.current = true;

    createConversation(
      { patient_partner_id: patientPartner.id },
      (conversation) => {
        const navigate = () => {
          cleanupSession();
          const firstMessage = buildMentorFirstMessage(patientPartner.name, firstMessageAnswer);
          const query = firstMessage ? `?${new URLSearchParams({ firstMessage }).toString()}` : '';
          history.push(`/member/conversations/${conversation.uuid}${query}`);
        };

        if (mentorMatch?.uuid) {
          updateMatchAndSet({
            uuid: mentorMatch.uuid,
            params: { conversation_id: conversation.id },
            setMentorMatch,
            callback: navigate,
          });
        } else {
          navigate();
        }
      },
      () => {
        // Reset so the user can retry (Continue click) after a transient
        // failure creating the conversation.
        handoffStartedRef.current = false;
      }
    );
  };

  const handoffRef = useRef(handoff);
  handoffRef.current = handoff;

  if (noMentorFound) {
    const copyProvided = noMentorHeading !== undefined || noMentorBody !== undefined;

    return (
      <NoMentorFoundMessage
        heading={copyProvided ? noMentorHeading || '' : COMMERCIAL_NO_MENTOR_HEADING}
        body={copyProvided ? noMentorBody || '' : COMMERCIAL_NO_MENTOR_BODY}
      />
    );
  }

  const mentor = patientPartner
    ? {
        name: patientPartner.name,
        state: patientPartner.profile?.state,
        photoUrl: patientPartner.profile?.photo,
        meta: buildMentorMeta(patientPartner),
        story: patientPartner.bio,
      }
    : undefined;

  return (
    <SignupMentorMatch
      view={view}
      progress={progress}
      activeStepIndex={activeStepIndex}
      mentor={mentor}
      onContinue={() => handoffRef.current()}
      onClose={() => handoffRef.current()}
    />
  );
}
